import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { noop, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import * as v from 'valibot';
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';
import {
	Calendar,
	CalendarDays,
	Check,
	ChevronDown,
	ChevronRight,
	Database,
	FileText,
	Heart,
	Info,
	MapPin,
	Percent,
	Receipt,
	TrendingUp,
	X,
} from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { Card } from '@worldcoo/ui/components/card';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@worldcoo/ui/components/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@worldcoo/ui/components/popover';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { cn, formatCurrency, formatCurrencyFromCents } from '@worldcoo/ui/lib/utils';
import { AnimateNumber } from '@worldcoo/ui/custom/animate-number';
import type { Locale } from '@worldcoo/monolith/core';
import {
	useNgoDashboardCampaignsQuery,
	useNgoDashboardFiltersQuery,
	useNgoDashboardHighlightsQuery,
	useNgoDashboardKpisQuery,
	useNgoDashboardMonthlyEvolutionQuery,
	useNgoDashboardTopCampaignsQuery,
	useNgoDashboardTopCitiesQuery,
	type NgoDashboardCampaignsFilters,
	type NgoDashboardSectionFilters,
} from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { MainSectionHeader, MainSectionContent } from './-components/main-section';
import { DateRangeFilter, SingleComboboxFilter, StatusFilter } from './-components/dashboard-filters';
import { FilterMultiSelect } from '@worldcoo/ui/components/filter-multi-select';
import {
	DashboardSectionBoundary,
	DashboardSectionEmptyState,
	DashboardSectionErrorState,
} from './-components/dashboard-section-states';

const CAMPAIGNS_PAGE_SIZE = 6;

type Scope = 'all' | 'myPortal' | 'worldcoo';
type CampaignStatus = 'notFundraising' | 'active' | 'paused' | 'funded';
type FilterKey =
	| 'date'
	| 'campaign'
	| 'merchant'
	| 'city'
	| 'tableDate'
	| 'tableCampaign'
	| 'tableMerchant'
	| 'tableStatus';

const sortKeys = ['name', 'revenue', 'funding', 'status', 'startDate', 'endDate'] as const;
type CampaignSortKey = (typeof sortKeys)[number];

const searchSchema = v.object({
	scope: v.fallback(v.optional(v.picklist(['all', 'myPortal', 'worldcoo'])), 'all'),
	// Top-of-page filters: scope KPIs, charts and highlights only.
	dateFrom: v.fallback(v.optional(v.string()), undefined),
	dateTo: v.fallback(v.optional(v.string()), undefined),
	campaignIds: v.fallback(v.optional(v.array(v.string())), undefined),
	merchantId: v.fallback(v.optional(v.string()), undefined),
	city: v.fallback(v.optional(v.string()), undefined),
	// Campaigns-table filters: independent from the page-level filters above.
	tableDateFrom: v.fallback(v.optional(v.string()), undefined),
	tableDateTo: v.fallback(v.optional(v.string()), undefined),
	tableCampaignIds: v.fallback(v.optional(v.array(v.string())), undefined),
	tableMerchantId: v.fallback(v.optional(v.string()), undefined),
	tableStatus: v.fallback(v.optional(v.picklist(['active', 'notFundraising', 'paused', 'funded'])), undefined),
	multiNgoNoticeDismissed: v.fallback(v.optional(v.boolean()), undefined),
	page: v.fallback(v.optional(v.number()), undefined),
	sortKey: v.fallback(v.optional(v.picklist(sortKeys)), undefined),
	sortOrder: v.fallback(v.optional(v.picklist(['asc', 'desc'])), undefined),
});

type SearchParams = v.InferOutput<typeof searchSchema>;

export const Route = createFileRoute('/ngos/dashboard')({
	validateSearch: searchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) => {
		const sectionInput = buildSectionInput(deps);
		const campaignsInput = buildCampaignsInput(deps);
		context.queryClient.ensureQueryData(trpc.ngo.getDashboardFilters.queryOptions()).catch(noop);
		context.queryClient.ensureQueryData(trpc.ngo.getPortals.queryOptions()).catch(noop);
		context.queryClient.ensureQueryData(trpc.ngo.getDashboardKpis.queryOptions(sectionInput)).catch(noop);
		context.queryClient.ensureQueryData(trpc.ngo.getDashboardMonthlyEvolution.queryOptions(sectionInput)).catch(noop);
		context.queryClient.ensureQueryData(trpc.ngo.getDashboardTopCampaigns.queryOptions(sectionInput)).catch(noop);
		context.queryClient.ensureQueryData(trpc.ngo.getDashboardTopCities.queryOptions(sectionInput)).catch(noop);
		context.queryClient.ensureQueryData(trpc.ngo.getDashboardHighlights.queryOptions(sectionInput)).catch(noop);
		context.queryClient.ensureQueryData(trpc.ngo.getDashboardCampaigns.queryOptions(campaignsInput)).catch(noop);
	},
	component: RouteComponent,
});

function buildSectionInput(search: SearchParams): NgoDashboardSectionFilters {
	return {
		scope: search.scope ?? 'all',
		filter: {
			dateFrom: search.dateFrom,
			dateTo: search.dateTo,
			campaignIds: search.campaignIds,
			merchantId: search.merchantId,
			city: search.city,
		},
	};
}

function buildCampaignsInput(search: SearchParams): NgoDashboardCampaignsFilters {
	const campaignsSort =
		search.sortKey && search.sortOrder ? { key: search.sortKey, order: search.sortOrder } : undefined;
	return {
		scope: search.scope ?? 'all',
		filter: {
			dateFrom: search.tableDateFrom,
			dateTo: search.tableDateTo,
			campaignIds: search.tableCampaignIds,
			merchantId: search.tableMerchantId,
			// City is not exposed in the table filter row — keep it undefined so the table
			// query stays independent of the page-level "City" filter.
			city: undefined,
		},
		campaignsPage: search.page ?? 1,
		campaignsPageSize: CAMPAIGNS_PAGE_SIZE,
		campaignsSort,
		campaignsStatus: search.tableStatus,
	};
}

function useDashboardFetching(): boolean {
	const queryClient = useQueryClient();
	const [count, setCount] = useState(0);
	useEffect(() => {
		const cache = queryClient.getQueryCache();
		const countDashboardFetching = () => {
			let n = 0;
			for (const q of cache.getAll()) {
				if (q.state.fetchStatus !== 'fetching') continue;
				const root = Array.isArray(q.queryKey) ? q.queryKey[0] : undefined;
				if (!Array.isArray(root)) continue;
				if (root.join('.').startsWith('ngo.getDashboard')) n++;
			}
			return n;
		};
		setCount(countDashboardFetching());
		return cache.subscribe(() => setCount(countDashboardFetching()));
	}, [queryClient]);
	return count > 0;
}

function RouteComponent() {
	const { t, i18n } = useLingui();
	const locale = (i18n.locale || 'en') as Locale;
	const search = Route.useSearch();
	const navigate = useNavigate({ from: '/ngos/dashboard' });
	const sectionInput = buildSectionInput(search);
	const campaignsInput = buildCampaignsInput(search);
	const hasCampaignFilter = (search.campaignIds?.length ?? 0) > 0;
	// True while any dashboard query is in flight. Subscribed directly to the query
	// cache: `useIsFetching` didn't reliably reflect suspense refetches in the
	// React 19 + useDeferredValue setup, so we watch the cache subscription manually.
	const isLoading = useDashboardFetching();

	// Which filter pill triggered the current navigation, so the spinner can sit inside
	// that pill. Cleared on the loading → idle transition; not on first render, where
	// `isLoading` is already `false`.
	const [pendingFilter, setPendingFilter] = useState<FilterKey | null>(null);
	const prevLoadingRef = useRef(false);
	useEffect(() => {
		if (prevLoadingRef.current && !isLoading) setPendingFilter(null);
		prevLoadingRef.current = isLoading;
	}, [isLoading]);
	const isFilterLoading = (key: FilterKey) => isLoading && pendingFilter === key;

	const setScope = (scope: Scope) => {
		// Scope affects both the page-level widgets and the table, so reset table pagination.
		navigate({ search: { ...search, scope, page: undefined } });
	};

	// Page-level filter changes don't touch the campaigns table's page state — the two
	// filter rows operate independently.
	const updateTopFilters = (key: FilterKey, updates: Partial<SearchParams>) => {
		setPendingFilter(key);
		navigate({ search: { ...search, ...updates } });
	};

	// Table-only filter changes reset the table's page; they don't touch the page-level
	// filter state.
	const updateTableFilters = (key: FilterKey, updates: Partial<SearchParams>) => {
		setPendingFilter(key);
		navigate({ search: { ...search, ...updates, page: undefined } });
	};

	return (
		<>
			<MainSectionHeader
				title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`My results`}</h1>}
				actions={
					<DashboardSectionBoundary
						fallback={<ScopeToggleSkeleton />}
						errorRender={() => (
							<ScopeToggle value={search.scope ?? 'all'} onValueChange={setScope} includeMyPortal={false} />
						)}
					>
						<ScopeToggleGate value={search.scope ?? 'all'} onValueChange={setScope} />
					</DashboardSectionBoundary>
				}
			/>
			<MainSectionContent>
				<div className="flex flex-col gap-6 p-4 md:p-6">
					<FiltersRow search={search} onChange={updateTopFilters} isFilterLoading={isFilterLoading} />
					{!search.multiNgoNoticeDismissed && (
						<DashboardSectionBoundary fallback={null} errorRender={() => null}>
							<HiddenMultiNgoCampaignsNoticeGate
								onDismiss={() => navigate({ search: { ...search, multiNgoNoticeDismissed: true } })}
							/>
						</DashboardSectionBoundary>
					)}
					<div className="flex flex-col gap-6">
						<KpiSection input={sectionInput} locale={locale} />
						<MonthlyEvolutionSection input={sectionInput} locale={locale} />
						<TopListsRow input={sectionInput} locale={locale} hasCampaignFilter={hasCampaignFilter} />
						<HighlightsSection input={sectionInput} locale={locale} hasCampaignFilter={hasCampaignFilter} />
						<CampaignsSection
							input={campaignsInput}
							locale={locale}
							search={search}
							onFilterChange={updateTableFilters}
							isFilterLoading={isFilterLoading}
							sortKey={search.sortKey ?? 'startDate'}
							sortOrder={search.sortOrder ?? 'desc'}
							onSortChange={(key) => {
								// Toggle order when clicking the active column; otherwise pick a sensible
								// default (desc for numeric/date columns, asc for name/status).
								const numericOrDate: CampaignSortKey[] = ['revenue', 'startDate', 'endDate'];
								const defaultOrder = numericOrDate.includes(key) ? 'desc' : 'asc';
								const nextOrder = search.sortKey === key ? (search.sortOrder === 'asc' ? 'desc' : 'asc') : defaultOrder;
								navigate({ search: { ...search, sortKey: key, sortOrder: nextOrder, page: undefined } });
							}}
							onPageChange={(page) => navigate({ search: { ...search, page } })}
						/>
					</div>
				</div>
			</MainSectionContent>
		</>
	);
}

function HiddenMultiNgoCampaignsNoticeGate(props: { onDismiss: () => void }) {
	const filters = useNgoDashboardFiltersQuery();
	if (filters.hiddenMultiNgoCampaigns.length === 0) return null;
	return <HiddenMultiNgoCampaignsNotice campaigns={filters.hiddenMultiNgoCampaigns} onDismiss={props.onDismiss} />;
}

function HiddenMultiNgoCampaignsNotice(props: {
	campaigns: {
		id: string;
		name: string;
		merchants: { alias: string; portalUrl: string }[];
		projectId: string | null;
	}[];
	onDismiss: () => void;
}) {
	const { t } = useLingui();
	const isSingular = props.campaigns.length === 1;
	return (
		<div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
			<Info className="mt-0.5 size-4 shrink-0 text-blue-600" strokeWidth={2} />
			<div className="flex flex-1 flex-col text-sm">
				<span className="font-medium text-blue-700">{t`Important notice`}</span>
				<span className="text-blue-600">
					{isSingular
						? t`The following campaign is shared with other NGOs and isn't shown yet. It'll appear once the campaign finishes and the distribution between NGOs is defined:`
						: t`The following campaigns are shared with other NGOs and aren't shown yet. They'll appear once the campaigns finish and the distribution between NGOs is defined:`}
				</span>
				<ul className="mt-1.5 flex flex-col gap-1.5">
					{props.campaigns.map((campaign) => (
						<li key={campaign.id} className="flex items-center gap-1.5 text-sm">
							<span className="size-1 shrink-0 rounded-full bg-blue-400" />
							{campaign.projectId ? (
								<Link
									to="/ngos/projects/$projectId"
									params={{ projectId: campaign.projectId }}
									className="font-medium text-blue-900 underline hover:text-blue-700"
								>
									{campaign.name}
								</Link>
							) : (
								<span className="font-medium text-blue-900">{campaign.name}</span>
							)}
							{campaign.merchants.length > 0 && (
								<span className="text-blue-500">
									(
									{campaign.merchants.map((merchant, i) => (
										<span key={merchant.portalUrl}>
											<a
												href={merchant.portalUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="text-blue-600 underline hover:text-blue-800"
											>
												{merchant.alias}
											</a>
											{i < campaign.merchants.length - 1 ? ', ' : ''}
										</span>
									))}
									)
								</span>
							)}
						</li>
					))}
				</ul>
			</div>
			<button
				type="button"
				onClick={props.onDismiss}
				aria-label={t`Dismiss notice`}
				className="-m-1 cursor-pointer rounded-md p-1 text-blue-600 transition-colors hover:text-blue-800"
			>
				<X className="size-4" />
			</button>
		</div>
	);
}

// Wraps ScopeToggle so the "My Portal" option is only shown when the NGO actually
// owns at least one active portal. Uses `useSuspenseQuery` so the option set is
// stable on first render (the portals query is prefetched in the route loader).
function ScopeToggleGate(props: { value: Scope; onValueChange: (value: Scope) => void }) {
	const { data } = useSuspenseQuery(trpc.ngo.getPortals.queryOptions());
	const hasPortals = 'portals' in data ? data.portals.length > 0 : false;
	const { value, onValueChange } = props;

	// If the URL pins us to "myPortal" but the NGO no longer has portals, fall back to "all".
	useEffect(() => {
		if (!hasPortals && value === 'myPortal') {
			onValueChange('all');
		}
	}, [hasPortals, value, onValueChange]);

	return <ScopeToggle value={value} onValueChange={onValueChange} includeMyPortal={hasPortals} />;
}

function ScopeToggleSkeleton() {
	return (
		<div className="flex items-center gap-1">
			<Skeleton className="h-9 w-12 rounded-full" />
			<Skeleton className="h-9 w-20 rounded-full" />
			<Skeleton className="h-9 w-24 rounded-full" />
		</div>
	);
}

function ScopeToggle(props: { value: Scope; onValueChange: (value: Scope) => void; includeMyPortal: boolean }) {
	const { t } = useLingui();
	const options: { value: Scope; label: string }[] = [
		{ value: 'all', label: t`All` },
		{ value: 'worldcoo', label: t`Worldcoo` },
		...(props.includeMyPortal ? [{ value: 'myPortal' as const, label: t`My Portal` }] : []),
	];

	return (
		<div className="flex items-center gap-1">
			{options.map((option) => {
				const isActive = props.value === option.value;
				return (
					<button
						key={option.value}
						type="button"
						onClick={() => props.onValueChange(option.value)}
						className={cn(
							'h-9 cursor-pointer rounded-full px-4 text-sm font-medium transition-colors',
							isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted',
						)}
					>
						{option.label}
					</button>
				);
			})}
		</div>
	);
}

function FiltersRow(props: {
	search: SearchParams;
	onChange: (key: FilterKey, updates: Partial<SearchParams>) => void;
	isFilterLoading: (key: FilterKey) => boolean;
}) {
	const { t } = useLingui();
	return (
		<div className="flex flex-wrap items-center gap-3">
			<span className="text-foreground text-base font-medium tracking-[-0.2px]">{t`Filters`}</span>
			<DashboardSectionBoundary
				fallback={
					<div className="flex flex-wrap items-center gap-2">
						<Skeleton className="h-11 w-24 rounded-xl" />
						<Skeleton className="h-11 w-32 rounded-xl" />
						<Skeleton className="h-11 w-32 rounded-xl" />
						<Skeleton className="h-11 w-28 rounded-xl" />
						<Skeleton className="h-11 w-24 rounded-xl" />
					</div>
				}
				retryQueryKey={trpc.ngo.getDashboardFilters.queryKey()}
				errorRender={(retry) => (
					<button
						type="button"
						onClick={retry}
						className="text-muted-foreground hover:text-foreground cursor-pointer text-xs"
					>
						{t`Couldn't load filters. Try again.`}
					</button>
				)}
			>
				<FiltersControls search={props.search} onChange={props.onChange} isFilterLoading={props.isFilterLoading} />
			</DashboardSectionBoundary>
		</div>
	);
}

function FiltersControls(props: {
	search: SearchParams;
	onChange: (key: FilterKey, updates: Partial<SearchParams>) => void;
	isFilterLoading: (key: FilterKey) => boolean;
}) {
	const { t } = useLingui();
	const filters = useNgoDashboardFiltersQuery();
	const campaignOptions = filters.campaigns.map((c) => ({ value: c.id, label: c.name }));
	const merchantOptions = filters.merchants.map((m) => ({ value: m.id, label: m.alias ?? m.id }));
	const cityOptions = filters.cities.map((city) => ({ value: city, label: city }));

	return (
		<div className="flex flex-wrap items-center gap-2">
			<DateRangeFilter
				dateFrom={props.search.dateFrom}
				dateTo={props.search.dateTo}
				onChange={({ dateFrom, dateTo }) => props.onChange('date', { dateFrom, dateTo })}
				isLoading={props.isFilterLoading('date')}
			/>
			<FilterMultiSelect
				variant="pill"
				options={campaignOptions}
				values={props.search.campaignIds ?? []}
				onChange={(ids) => props.onChange('campaign', { campaignIds: ids.length > 0 ? ids : undefined })}
				placeholder={t`Campaign`}
				searchPlaceholder={t`Search campaigns...`}
				emptyText={t`No campaigns`}
			/>
			<SingleComboboxFilter
				label={t`Merchant`}
				searchPlaceholder={t`Search merchants...`}
				emptyMessage={t`No merchants`}
				allLabel={t`All merchants`}
				options={merchantOptions}
				value={props.search.merchantId}
				onChange={(merchantId) => props.onChange('merchant', { merchantId })}
				isLoading={props.isFilterLoading('merchant')}
			/>
			<SingleComboboxFilter
				label={t`City`}
				searchPlaceholder={t`Search cities...`}
				emptyMessage={t`No cities`}
				allLabel={t`All cities`}
				options={cityOptions}
				value={props.search.city}
				onChange={(city) => props.onChange('city', { city })}
				isLoading={props.isFilterLoading('city')}
			/>
		</div>
	);
}

// Large enough to fetch the full filtered campaign set for CSV export in one round-trip.
// Matches the schema cap on `trpc.ngo.getDashboardCampaigns`.
const CAMPAIGNS_CSV_PAGE_SIZE = 10_000;

function DownloadDropdown(props: { input: NgoDashboardCampaignsFilters }) {
	const { t } = useLingui();
	const queryClient = useQueryClient();
	const [isDownloading, setIsDownloading] = useState(false);

	const downloadCsv = async () => {
		setIsDownloading(true);
		try {
			// Refetch with the current table filters but no pagination — the CSV always
			// reflects the full filtered view, not the page on screen.
			const fullInput: NgoDashboardCampaignsFilters = {
				...props.input,
				campaignsPage: 1,
				campaignsPageSize: CAMPAIGNS_CSV_PAGE_SIZE,
			};
			const data = await queryClient.fetchQuery(trpc.ngo.getDashboardCampaigns.queryOptions(fullInput));
			if ('unauthenticated' in data) return;
			const csv = buildCampaignsCsv(data.items, {
				nameHeader: t`Campaign name`,
				revenueHeader: t`Revenue (EUR)`,
				goalHeader: t`Goal (EUR)`,
				fundingHeader: t`Funding`,
				statusHeader: t`Status`,
				startDateHeader: t`Start date`,
				endDateHeader: t`End date`,
				merchantsHeader: t`Merchant`,
				portalsHeader: t`Portals`,
			});
			triggerDownload(csv, `campaigns-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv;charset=utf-8');
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="lg"
					className="border-border h-10 rounded-xl px-4 text-sm font-medium"
					disabled={isDownloading}
				>
					{isDownloading ? t`Preparing...` : t`Download`}
					<ChevronDown className="size-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				<DropdownMenuItem onSelect={downloadCsv} disabled={isDownloading}>{t`CSV`}</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

// ── KPI section ─────────────────────────────────────────────────────────────

function KpiSection(props: { input: NgoDashboardSectionFilters; locale: Locale }) {
	const { t } = useLingui();
	return (
		<DashboardSectionBoundary
			fallback={<KpiSkeleton />}
			retryQueryKey={trpc.ngo.getDashboardKpis.queryKey(props.input)}
			errorRender={(retry, isRetrying) => (
				<Card className="items-center justify-center p-8">
					<DashboardSectionErrorState
						title={t`We couldn't retrieve top metrics.`}
						onRetry={retry}
						isRetrying={isRetrying}
					/>
				</Card>
			)}
		>
			<KpiRow input={props.input} locale={props.locale} />
		</DashboardSectionBoundary>
	);
}

function KpiRow(props: { input: NgoDashboardSectionFilters; locale: Locale }) {
	const { t } = useLingui();
	const kpis = useNgoDashboardKpisQuery(props.input);

	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-[minmax(max-content,1fr)_repeat(3,minmax(0,1fr))]">
			<KpiCard
				label={t`Total settled`}
				value={kpis.totalAmountCents / 100}
				locale={props.locale}
				decimalDigits={2}
				suffix="€"
				icon={<Database className="size-6 text-[#492409]" strokeWidth={1.5} />}
				iconBg="bg-[#ffede0]"
			/>
			<KpiCard
				label={t`Number of donations`}
				value={kpis.donationsCount}
				locale={props.locale}
				decimalDigits={0}
				icon={<Heart className="size-6 text-[#064b38]" strokeWidth={1.5} />}
				iconBg="bg-[#d5eee7]"
			/>
			<KpiCard
				label={t`Average donation`}
				value={kpis.averageDonationCents / 100}
				locale={props.locale}
				decimalDigits={2}
				suffix="€"
				icon={<Percent className="size-6 text-[#4a3c04]" strokeWidth={1.5} />}
				iconBg="bg-[#f9edbf]"
			/>
			<KpiCard
				label={t`Total campaigns`}
				value={kpis.campaignsCount}
				locale={props.locale}
				decimalDigits={0}
				icon={<FileText className="size-6 text-[#292d32]" strokeWidth={1.5} />}
				iconBg="bg-[#f5dbff]"
			/>
		</div>
	);
}

function KpiSkeleton() {
	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
			{Array.from({ length: 4 }).map((_, i) => (
				// oxlint-disable-next-line react/no-array-index-key
				<Skeleton key={`kpi-${i}`} className="h-24 rounded-xl" />
			))}
		</div>
	);
}

function KpiCard(props: {
	label: string;
	value: number;
	locale: Locale;
	decimalDigits: number;
	suffix?: string;
	icon: React.ReactNode;
	iconBg: string;
}) {
	return (
		<Card className="shadow-warm flex-row items-center gap-4 p-4 md:gap-5 md:p-5">
			<div className={cn('flex size-12 shrink-0 items-center justify-center rounded-xl md:size-14', props.iconBg)}>
				{props.icon}
			</div>
			<div className="flex min-w-0 flex-col gap-1">
				<span className="text-muted-foreground text-xs font-medium tracking-[0.08em] uppercase">{props.label}</span>
				<AnimateNumber
					locale={props.locale}
					value={props.value}
					decimalDigits={props.decimalDigits}
					suffix={props.suffix}
					className="text-foreground text-2xl font-medium tracking-[-0.5px] md:text-3xl"
				/>
			</div>
		</Card>
	);
}

// ── Monthly evolution chart ─────────────────────────────────────────────────

type EvolutionMetric = 'amount' | 'donations';

function MonthlyEvolutionSection(props: { input: NgoDashboardSectionFilters; locale: Locale }) {
	const { t } = useLingui();
	const [metric, setMetric] = useState<EvolutionMetric>('amount');
	const subtitle = metric === 'amount' ? t`Amount settled per month` : t`Donations per month`;

	return (
		<Card className="shadow-warm gap-4 p-5">
			<div className="flex items-start justify-between gap-3">
				<div className="flex flex-col gap-1">
					<h3 className="text-foreground text-base font-medium tracking-[-0.2px]">{t`Monthly evolution`}</h3>
					<p className="text-muted-foreground text-sm">{subtitle}</p>
				</div>
				<EvolutionMetricToggle value={metric} onValueChange={setMetric} />
			</div>
			<div className="h-64 w-full">
				<DashboardSectionBoundary
					fallback={<Skeleton className="h-full w-full rounded-lg" />}
					retryQueryKey={trpc.ngo.getDashboardMonthlyEvolution.queryKey(props.input)}
					errorRender={(retry, isRetrying) => (
						<DashboardSectionErrorState
							title={t`We couldn't retrieve chart data.`}
							onRetry={retry}
							isRetrying={isRetrying}
							className="h-full justify-center"
						/>
					)}
				>
					<MonthlyEvolutionChart input={props.input} locale={props.locale} metric={metric} />
				</DashboardSectionBoundary>
			</div>
		</Card>
	);
}

function MonthlyEvolutionChart(props: { input: NgoDashboardSectionFilters; locale: Locale; metric: EvolutionMetric }) {
	const { t } = useLingui();
	const points = useNgoDashboardMonthlyEvolutionQuery(props.input);
	const data = points.map((p) => ({
		month: formatShortMonth(p.month, props.locale),
		amount: Math.round(p.amountCents / 100),
		donations: p.donationsCount,
	}));

	if (data.length === 0) {
		return (
			<DashboardSectionEmptyState
				title={t`No data to show yet.`}
				description={t`Trends will appear as donations come in.`}
				className="h-full justify-center"
			/>
		);
	}

	const yTickFormatter = (value: number) =>
		props.metric === 'amount' ? formatCurrency(props.locale, value) : value.toLocaleString(props.locale);

	return (
		<ResponsiveContainer width="100%" height="100%">
			<AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
				<defs>
					<linearGradient id="evolutionFill" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="#7cc29a" stopOpacity={0.55} />
						<stop offset="100%" stopColor="#7cc29a" stopOpacity={0.05} />
					</linearGradient>
				</defs>
				<CartesianGrid stroke="#eef0ee" vertical={false} />
				<XAxis
					dataKey="month"
					tickLine={false}
					axisLine={false}
					tick={{ fill: '#8a8f8a', fontSize: 11 }}
					interval="preserveStartEnd"
				/>
				<YAxis
					tickLine={false}
					axisLine={false}
					tick={{ fill: '#8a8f8a', fontSize: 11 }}
					width={56}
					tickFormatter={yTickFormatter}
				/>
				<RechartsTooltip
					cursor={{ stroke: '#cfd4cf', strokeWidth: 1 }}
					content={<EvolutionTooltip locale={props.locale} metric={props.metric} />}
					animationDuration={150}
				/>
				<Area
					type="monotone"
					dataKey={props.metric}
					stroke="#4fa377"
					strokeWidth={2}
					fill="url(#evolutionFill)"
					activeDot={{ r: 4, fill: '#4fa377', stroke: '#ffffff', strokeWidth: 2 }}
				/>
			</AreaChart>
		</ResponsiveContainer>
	);
}

function EvolutionMetricToggle(props: { value: EvolutionMetric; onValueChange: (next: EvolutionMetric) => void }) {
	const { t } = useLingui();
	const options: { value: EvolutionMetric; label: string }[] = [
		{ value: 'amount', label: t`Amount` },
		{ value: 'donations', label: t`Donations` },
	];
	return (
		<div className="inline-flex items-center gap-1">
			{options.map((option) => {
				const isActive = props.value === option.value;
				return (
					<button
						key={option.value}
						type="button"
						onClick={() => props.onValueChange(option.value)}
						className={cn(
							'h-9 cursor-pointer rounded-full px-4 text-sm font-medium transition-colors',
							isActive ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
						)}
					>
						{option.label}
					</button>
				);
			})}
		</div>
	);
}

function EvolutionTooltip(props: {
	active?: boolean;
	payload?: Array<{ payload: { month: string; amount: number; donations: number } }>;
	locale: Locale;
	metric: EvolutionMetric;
}) {
	if (!props.active || !props.payload?.length) return null;
	const point = props.payload[0]?.payload;
	if (!point) return null;
	const value =
		props.metric === 'amount'
			? formatCurrency(props.locale, point.amount)
			: point.donations.toLocaleString(props.locale);
	return (
		<div className="border-border bg-background flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs shadow-md">
			<span className="text-muted-foreground">{point.month}</span>
			<span className="text-foreground font-medium tabular-nums">{value}</span>
		</div>
	);
}

// ── Top lists ───────────────────────────────────────────────────────────────

function TopListsRow(props: { input: NgoDashboardSectionFilters; locale: Locale; hasCampaignFilter: boolean }) {
	const { t } = useLingui();
	// "Top campaigns" only makes sense when comparing across the full campaign set —
	// hide it when the user has narrowed to a specific campaign.
	if (props.hasCampaignFilter) {
		return (
			<div className="grid grid-cols-1 gap-4">
				<TopBarListCard title={t`Top cities`} subtitle={t`Total amount collected per city`}>
					<DashboardSectionBoundary
						fallback={<TopBarListSkeleton />}
						retryQueryKey={trpc.ngo.getDashboardTopCities.queryKey(props.input)}
						errorRender={(retry, isRetrying) => (
							<DashboardSectionErrorState
								title={t`We couldn't retrieve city data.`}
								onRetry={retry}
								isRetrying={isRetrying}
								className="py-6"
							/>
						)}
					>
						<TopCitiesList input={props.input} locale={props.locale} />
					</DashboardSectionBoundary>
				</TopBarListCard>
			</div>
		);
	}
	return (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
			<TopBarListCard title={t`Top campaigns`} subtitle={t`Total amount collected per campaign`}>
				<DashboardSectionBoundary
					fallback={<TopBarListSkeleton />}
					retryQueryKey={trpc.ngo.getDashboardTopCampaigns.queryKey(props.input)}
					errorRender={(retry, isRetrying) => (
						<DashboardSectionErrorState
							title={t`We couldn't retrieve campaign data.`}
							onRetry={retry}
							isRetrying={isRetrying}
							className="py-6"
						/>
					)}
				>
					<TopCampaignsList input={props.input} locale={props.locale} />
				</DashboardSectionBoundary>
			</TopBarListCard>
			<TopBarListCard title={t`Top cities`} subtitle={t`Total amount collected per city`}>
				<DashboardSectionBoundary
					fallback={<TopBarListSkeleton />}
					retryQueryKey={trpc.ngo.getDashboardTopCities.queryKey(props.input)}
					errorRender={(retry, isRetrying) => (
						<DashboardSectionErrorState
							title={t`We couldn't retrieve city data.`}
							onRetry={retry}
							isRetrying={isRetrying}
							className="py-6"
						/>
					)}
				>
					<TopCitiesList input={props.input} locale={props.locale} />
				</DashboardSectionBoundary>
			</TopBarListCard>
		</div>
	);
}

function TopBarListCard(props: { title: string; subtitle: string; children: React.ReactNode }) {
	return (
		<Card className="shadow-warm gap-4 p-5">
			<div className="flex flex-col gap-1">
				<h3 className="text-foreground text-base font-medium tracking-[-0.2px]">{props.title}</h3>
				<p className="text-muted-foreground text-sm">{props.subtitle}</p>
			</div>
			{props.children}
		</Card>
	);
}

function TopBarListSkeleton() {
	return (
		<div className="flex flex-col gap-1.5">
			{Array.from({ length: 5 }).map((_, i) => (
				// oxlint-disable-next-line react/no-array-index-key
				<Skeleton key={`top-${i}`} className="h-9 rounded-md" />
			))}
		</div>
	);
}

function TopCampaignsList(props: { input: NgoDashboardSectionFilters; locale: Locale }) {
	const { t } = useLingui();
	const items = useNgoDashboardTopCampaignsQuery(props.input);
	if (items.length === 0) {
		return (
			<DashboardSectionEmptyState
				title={t`No campaigns data available.`}
				description={t`You'll see the top campaigns once activity starts.`}
				className="py-6"
			/>
		);
	}
	return <TopBarList items={items.map((c) => ({ label: c.name, amountCents: c.amountCents }))} locale={props.locale} />;
}

function TopCitiesList(props: { input: NgoDashboardSectionFilters; locale: Locale }) {
	const { t } = useLingui();
	const items = useNgoDashboardTopCitiesQuery(props.input);
	if (items.length === 0) {
		return (
			<DashboardSectionEmptyState
				title={t`No city data available.`}
				description={t`You'll see where donations come from once activity starts.`}
				className="py-6"
			/>
		);
	}
	return <TopBarList items={items.map((c) => ({ label: c.city, amountCents: c.amountCents }))} locale={props.locale} />;
}

function TopBarList(props: { items: { label: string; amountCents: number }[]; locale: Locale }) {
	const maxAmount = Math.max(...props.items.map((item) => item.amountCents), 1);
	return (
		<ul className="flex flex-col gap-1.5">
			{props.items.map((item) => {
				const widthPct = Math.max((item.amountCents / maxAmount) * 100, 8);
				return (
					<li key={item.label} className="relative flex h-9 items-center overflow-hidden rounded-md">
						<div className="absolute inset-y-0 left-0 rounded-md bg-[#ffedd5]" style={{ width: `${widthPct}%` }} />
						<div className="relative flex w-full items-center justify-between px-3">
							<span className="text-foreground truncate text-sm">{item.label}</span>
							<span className="text-foreground text-sm tabular-nums">
								{formatEurosCompact(item.amountCents, props.locale)}
							</span>
						</div>
					</li>
				);
			})}
		</ul>
	);
}

// ── Highlights ──────────────────────────────────────────────────────────────

function HighlightsSection(props: { input: NgoDashboardSectionFilters; locale: Locale; hasCampaignFilter: boolean }) {
	const { t } = useLingui();
	return (
		<div className="flex flex-col gap-3">
			<DashboardSectionBoundary
				fallback={<HighlightsSkeleton hasCampaignFilter={props.hasCampaignFilter} />}
				retryQueryKey={trpc.ngo.getDashboardHighlights.queryKey(props.input)}
				errorRender={(retry, isRetrying) => (
					<Card className="items-center justify-center p-8">
						<DashboardSectionErrorState
							title={t`We couldn't retrieve highlights.`}
							onRetry={retry}
							isRetrying={isRetrying}
						/>
					</Card>
				)}
			>
				<HighlightsRow input={props.input} locale={props.locale} hasCampaignFilter={props.hasCampaignFilter} />
			</DashboardSectionBoundary>
		</div>
	);
}

function HighlightsSkeleton(props: { hasCampaignFilter: boolean }) {
	const count = props.hasCampaignFilter ? 4 : 5;
	return (
		<div
			className={cn(
				'grid grid-cols-2 gap-4',
				props.hasCampaignFilter ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3 xl:grid-cols-5',
			)}
		>
			{Array.from({ length: count }).map((_, i) => (
				// oxlint-disable-next-line react/no-array-index-key
				<Skeleton key={`hl-${i}`} className="h-40 rounded-xl" />
			))}
		</div>
	);
}

function HighlightsRow(props: { input: NgoDashboardSectionFilters; locale: Locale; hasCampaignFilter: boolean }) {
	const { t } = useLingui();
	const highlights = useNgoDashboardHighlightsQuery(props.input);
	const { topDonation, bestDay, mostGenerousCity, topMerchant, bestMonth } = highlights;
	const dash = '—';

	const donationsWord = t`donations`;
	const collectedWord = t`collected`;
	const bestDaySubtitle = bestDay
		? `${bestDay.donationsCount} ${donationsWord} - ${formatEurosRounded(bestDay.amountCents, props.locale)}`
		: '';
	const mostGenerousSubtitle = mostGenerousCity
		? `${formatEurosRounded(mostGenerousCity.amountCents, props.locale)} - ${mostGenerousCity.donationsCount} ${donationsWord}`
		: '';
	const topMerchantSubtitle = topMerchant
		? `${formatEurosRounded(topMerchant.amountCents, props.locale)} ${collectedWord}`
		: '';
	const bestMonthSubtitle = bestMonth
		? `${formatEurosRounded(bestMonth.amountCents, props.locale)} ${collectedWord}`
		: '';

	return (
		<div
			className={cn(
				'grid grid-cols-2 gap-4',
				props.hasCampaignFilter ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3 xl:grid-cols-5',
			)}
		>
			<HighlightCard
				label={t`Top donation`}
				value={topDonation ? formatEurosRounded(topDonation.amountCents, props.locale) : dash}
				numericValue={topDonation ? Math.round(topDonation.amountCents / 100) : undefined}
				locale={props.locale}
				suffix="€"
				subtitle={topDonation?.city ?? ''}
				icon={<TrendingUp className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
			/>
			<HighlightCard
				label={t`Best day`}
				value={bestDay ? formatShortDate(bestDay.date, props.locale) : dash}
				subtitle={bestDaySubtitle}
				icon={<CalendarDays className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
			/>
			<HighlightCard
				label={t`Most generous city`}
				value={mostGenerousCity?.city ?? dash}
				subtitle={mostGenerousSubtitle}
				icon={<MapPin className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
			/>
			{!props.hasCampaignFilter && (
				<HighlightCard
					label={t`Top merchant`}
					value={topMerchant?.alias ?? dash}
					subtitle={topMerchantSubtitle}
					icon={<Receipt className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
				/>
			)}
			<HighlightCard
				label={t`Best month`}
				value={bestMonth ? formatLongMonth(bestMonth.month, props.locale) : dash}
				subtitle={bestMonthSubtitle}
				icon={<Calendar className="size-5 text-[#6b4fa3]" strokeWidth={1.5} />}
			/>
		</div>
	);
}

function HighlightCard(props: {
	label: string;
	value: string;
	subtitle: string;
	icon: React.ReactNode;
	numericValue?: number;
	locale?: Locale;
	suffix?: string;
}) {
	return (
		<Card className="shadow-warm items-start gap-2 p-4 text-left md:p-5">
			<div className="flex size-10 items-center justify-center rounded-xl bg-[#e4d9f0]">{props.icon}</div>
			<span className="text-muted-foreground text-[11px] font-medium tracking-[0.08em] uppercase">{props.label}</span>
			{props.numericValue != null && props.locale ? (
				<AnimateNumber
					locale={props.locale}
					value={props.numericValue}
					decimalDigits={0}
					suffix={props.suffix}
					className="text-foreground text-2xl font-medium tracking-[-0.3px]"
				/>
			) : (
				<span className="text-foreground text-2xl font-medium tracking-[-0.3px] tabular-nums">{props.value}</span>
			)}
			<span className="text-muted-foreground text-xs">{props.subtitle || ' '}</span>
		</Card>
	);
}

// ── Campaigns table ─────────────────────────────────────────────────────────

type CampaignRow = {
	id: string;
	name: string;
	revenueCents: number;
	goalCents: number | null;
	funding: ('myPortal' | 'worldcoo')[];
	status: CampaignStatus;
	startDate: string | null;
	endDate: string | null;
	portals: PortalLink[];
	merchants: { id: string; alias: string }[];
};

type PortalLink = { portalId: string; label: string; url: string; accentColor: string | null };

function CampaignsSection(props: {
	input: NgoDashboardCampaignsFilters;
	locale: Locale;
	search: SearchParams;
	onFilterChange: (key: FilterKey, updates: Partial<SearchParams>) => void;
	sortKey: CampaignSortKey;
	sortOrder: 'asc' | 'desc';
	onSortChange: (key: CampaignSortKey) => void;
	onPageChange: (page: number) => void;
	isFilterLoading: (key: FilterKey) => boolean;
}) {
	const { t } = useLingui();
	return (
		<Card className="shadow-warm gap-0 p-0">
			<DashboardSectionBoundary
				fallback={<CampaignsHeaderSkeleton />}
				errorRender={() => <CampaignsHeader title={t`Campaigns`} />}
			>
				<CampaignsHeader
					title={t`Campaigns`}
					filters={
						<CampaignsTableFilters
							search={props.search}
							onChange={props.onFilterChange}
							isFilterLoading={props.isFilterLoading}
						/>
					}
					download={
						<DashboardSectionBoundary fallback={null} errorRender={() => null}>
							<CampaignsDownloadAction input={props.input} />
						</DashboardSectionBoundary>
					}
					search={props.search}
				/>
			</DashboardSectionBoundary>
			<DashboardSectionBoundary
				fallback={<CampaignsTableSkeleton />}
				retryQueryKey={trpc.ngo.getDashboardCampaigns.queryKey(props.input)}
				errorRender={(retry, isRetrying) => (
					<div className="flex flex-col items-center justify-center px-5 py-12">
						<DashboardSectionErrorState
							title={t`We couldn't load your campaigns.`}
							onRetry={retry}
							isRetrying={isRetrying}
						/>
					</div>
				)}
			>
				<CampaignsTable
					input={props.input}
					locale={props.locale}
					sortKey={props.sortKey}
					sortOrder={props.sortOrder}
					onSortChange={props.onSortChange}
					onPageChange={props.onPageChange}
				/>
			</DashboardSectionBoundary>
		</Card>
	);
}

function hasAnyCampaignFilter(search: SearchParams): boolean {
	return (
		(search.tableCampaignIds != null && search.tableCampaignIds.length > 0) ||
		!!search.tableDateFrom ||
		!!search.tableDateTo ||
		!!search.tableStatus ||
		!!search.tableMerchantId
	);
}

function CampaignsHeader(props: {
	title: string;
	filters?: React.ReactNode;
	download?: React.ReactNode;
	search?: SearchParams;
}) {
	// `useNgoDashboardFiltersQuery` is the cheapest source of "does this NGO have any
	// campaigns at all?" — it's already prefetched and shared with the global filter
	// row. When the NGO has zero campaigns AND no filter narrows the view, the controls
	// would only frame an empty state, so we collapse the header to just the title.
	const filters = useNgoDashboardFiltersQuery();
	const search = props.search;
	const hasCampaigns = filters.campaigns.length > 0;
	const filtersApplied = search ? hasAnyCampaignFilter(search) : false;
	const showControls = hasCampaigns || filtersApplied;
	return (
		<div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
			<h3 className="text-foreground text-base font-medium tracking-[-0.2px]">{props.title}</h3>
			{showControls && (props.filters || props.download) && (
				<div className="flex flex-wrap items-center gap-3">
					{props.filters}
					{props.filters && props.download && <span className="bg-border h-8 w-px" aria-hidden />}
					{props.download}
				</div>
			)}
		</div>
	);
}

function CampaignsHeaderSkeleton() {
	return (
		<div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
			<Skeleton className="h-6 w-28 rounded-md" />
			<div className="flex items-center gap-2">
				<Skeleton className="h-10 w-32 rounded-xl" />
				<Skeleton className="h-10 w-24 rounded-xl" />
				<Skeleton className="h-10 w-24 rounded-xl" />
				<Skeleton className="h-10 w-28 rounded-xl" />
			</div>
		</div>
	);
}

function CampaignsTableFilters(props: {
	search: SearchParams;
	onChange: (key: FilterKey, updates: Partial<SearchParams>) => void;
	isFilterLoading: (key: FilterKey) => boolean;
}) {
	const { t } = useLingui();
	const filters = useNgoDashboardFiltersQuery();
	const campaignOptions = filters.campaigns.map((c) => ({ value: c.id, label: c.name }));
	const merchantOptions = filters.merchants.map((m) => ({ value: m.id, label: m.alias ?? m.id }));
	return (
		<div className="flex flex-wrap items-center gap-2">
			<span className="text-muted-foreground text-sm">{t`Filters`}</span>
			<FilterMultiSelect
				variant="pill"
				options={campaignOptions}
				values={props.search.tableCampaignIds ?? []}
				onChange={(ids) => props.onChange('tableCampaign', { tableCampaignIds: ids.length > 0 ? ids : undefined })}
				placeholder={t`Campaign`}
				searchPlaceholder={t`Search campaigns...`}
				emptyText={t`No campaigns`}
			/>
			<DateRangeFilter
				dateFrom={props.search.tableDateFrom}
				dateTo={props.search.tableDateTo}
				onChange={({ dateFrom, dateTo }) =>
					props.onChange('tableDate', { tableDateFrom: dateFrom, tableDateTo: dateTo })
				}
				isLoading={props.isFilterLoading('tableDate')}
			/>
			<StatusFilter
				value={props.search.tableStatus}
				onChange={(tableStatus) => props.onChange('tableStatus', { tableStatus })}
				isLoading={props.isFilterLoading('tableStatus')}
			/>
			<SingleComboboxFilter
				label={t`Merchant`}
				searchPlaceholder={t`Search merchants...`}
				emptyMessage={t`No merchants`}
				allLabel={t`All merchants`}
				options={merchantOptions}
				value={props.search.tableMerchantId}
				onChange={(merchantId) => props.onChange('tableMerchant', { tableMerchantId: merchantId })}
				isLoading={props.isFilterLoading('tableMerchant')}
			/>
		</div>
	);
}

function CampaignsDownloadAction(props: { input: NgoDashboardCampaignsFilters }) {
	const data = useNgoDashboardCampaignsQuery(props.input);
	// `total` is the unpaginated row count, so empty filtered views (or NGOs with no
	// campaigns) hide the button even when the current page happens to have rows.
	if (data.total === 0) return null;
	return <DownloadDropdown input={props.input} />;
}

function CampaignsTableSkeleton() {
	return (
		<div className="px-5 py-6">
			<div className="flex flex-col gap-3">
				{Array.from({ length: 6 }).map((_, i) => (
					// oxlint-disable-next-line react/no-array-index-key
					<Skeleton key={`row-${i}`} className="h-10 rounded-md" />
				))}
			</div>
		</div>
	);
}

function CampaignsTable(props: {
	input: NgoDashboardCampaignsFilters;
	locale: Locale;
	sortKey: CampaignSortKey;
	sortOrder: 'asc' | 'desc';
	onSortChange: (key: CampaignSortKey) => void;
	onPageChange: (page: number) => void;
}) {
	const { t } = useLingui();
	const data = useNgoDashboardCampaignsQuery(props.input);
	const totalPages = Math.max(1, Math.ceil(data.total / CAMPAIGNS_PAGE_SIZE));
	// `useDeferredValue` inside the query hook keeps showing the previous page's data
	// while the new one is in flight, so the URL/intent page and the rendered page
	// disagree during the fetch. Use that mismatch to dim the body — it's the cheapest
	// "your click is doing something" signal without flipping to a skeleton.
	const isStale = data.page !== props.input.campaignsPage;

	const headerProps = (key: CampaignSortKey) => ({
		isSorted: props.sortKey === key,
		sortOrder: props.sortOrder,
		onSort: () => props.onSortChange(key),
	});

	if (data.items.length === 0 && data.total === 0) {
		// Distinguish "this NGO has no campaigns yet" (offer to create one) from
		// "filters narrowed the table to zero rows" (offer to clear the filters).
		const filtered =
			(props.input.filter.campaignIds?.length ?? 0) > 0 ||
			!!props.input.filter.dateFrom ||
			!!props.input.filter.dateTo ||
			!!props.input.filter.merchantId ||
			!!props.input.campaignsStatus;
		if (filtered) {
			return (
				<div className="flex flex-col items-center justify-center px-5 py-12">
					<DashboardSectionEmptyState
						title={t`No campaigns match the selected filters.`}
						description={t`Try removing or adjusting the filters above.`}
					/>
				</div>
			);
		}
		return (
			<div className="flex flex-col items-center justify-center px-5 py-12">
				<DashboardSectionEmptyState
					title={t`No campaigns to display.`}
					description={t`Create a campaign to start managing your fundraising.`}
					action={props.input.scope === 'myPortal' ? <CreateCampaignButton /> : undefined}
				/>
			</div>
		);
	}

	return (
		<>
			<div className="overflow-x-auto">
				<table className="w-full table-fixed text-sm">
					<colgroup>
						<col className="w-[16%]" />
						<col className="w-[14%]" />
						<col className="w-[14%]" />
						<col className="w-[12%]" />
						<col className="w-[8%]" />
						<col className="w-[12%]" />
						<col className="w-[12%]" />
						<col className="w-[12%]" />
					</colgroup>
					<thead>
						<tr className="bg-[#f5f5f3]">
							<TableHeaderCell label={t`Campaign name`} {...headerProps('name')} />
							<TableHeaderCell label={t`Start date`} {...headerProps('startDate')} />
							<TableHeaderCell label={t`End date`} {...headerProps('endDate')} />
							<TableHeaderCell label={t`Revenue`} {...headerProps('revenue')} />
							<TableHeaderCell label={t`Funding`} {...headerProps('funding')} />
							<TableHeaderCell label={t`Portal`} />
							<TableHeaderCell label={t`Merchant`} />
							<TableHeaderCell label={t`Status`} {...headerProps('status')} />
						</tr>
					</thead>
					<tbody
						className={cn('transition-opacity', isStale ? 'pointer-events-none opacity-50' : 'opacity-100')}
						aria-busy={isStale}
					>
						{data.items.map((campaign) => (
							<tr key={campaign.id}>
								<td className="px-5 py-3.5">
									<span className="text-foreground">{campaign.name}</span>
								</td>
								<td className="px-5 py-3.5 whitespace-nowrap">
									<DateCell iso={campaign.startDate} />
								</td>
								<td className="px-5 py-3.5 whitespace-nowrap">
									<DateCell iso={campaign.endDate} />
								</td>
								<td className="px-5 py-3.5">
									<div className="flex flex-wrap items-baseline gap-x-1.5">
										<span className="text-foreground whitespace-nowrap tabular-nums">
											{formatEurosCompact(campaign.revenueCents, props.locale)}
										</span>
										{campaign.goalCents != null && (
											<span className="text-muted-foreground text-xs whitespace-nowrap tabular-nums">
												{t`of`} {formatEurosCompact(campaign.goalCents, props.locale)}
											</span>
										)}
									</div>
								</td>
								<td className="px-5 py-3.5">
									<div className="flex flex-wrap items-center gap-2">
										{campaign.funding.length === 0 ? (
											<span className="text-muted-foreground text-xs">{t`None`}</span>
										) : (
											campaign.funding.map((f) => <FundingChip key={f} funding={f} />)
										)}
									</div>
								</td>
								<td className="px-5 py-3.5">
									<PortalLinks portals={campaign.portals} />
								</td>
								<td className="px-5 py-3.5">
									<MerchantsCell merchants={campaign.merchants} />
								</td>
								<td className="px-5 py-3.5">
									<StatusBadge status={campaign.status} />
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<div className="flex flex-col items-start gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
				<CampaignCountLine shown={data.items.length} total={data.total} />
				{totalPages > 1 && (
					<Pagination page={props.input.campaignsPage} totalPages={totalPages} onPageChange={props.onPageChange} />
				)}
			</div>
		</>
	);
}

function MerchantsCell(props: { merchants: { id: string; alias: string }[] }) {
	if (props.merchants.length === 0) {
		return <span className="text-muted-foreground text-xs">—</span>;
	}
	return (
		<div className="flex flex-col gap-1">
			{props.merchants.map((m) => (
				<span key={m.id} className="text-foreground truncate text-sm">
					{m.alias}
				</span>
			))}
		</div>
	);
}

function CreateCampaignButton() {
	const { t } = useLingui();
	const navigate = useNavigate();
	const { data } = useQuery(trpc.ngo.getPortals.queryOptions());
	const portals = data && 'portals' in data ? data.portals : undefined;

	const handleClick = () => {
		const onlyPortal = portals && portals.length === 1 ? portals[0] : undefined;
		if (onlyPortal) {
			navigate({
				to: '/ngos/portals/$portalId',
				params: { portalId: onlyPortal.id },
				search: { tab: 'projects' },
			});
			return;
		}
		navigate({ to: '/ngos/portals' });
	};

	return (
		<Button variant="outline-dark" onClick={handleClick}>
			{t`Create campaign`}
		</Button>
	);
}

function CampaignCountLine(props: { shown: number; total: number }) {
	const { t } = useLingui();
	const shown = props.shown;
	const total = props.total;
	return <span className="text-muted-foreground text-sm">{t`Showing ${shown} of ${total} campaigns`}</span>;
}

function DateCell(props: { iso: string | null }) {
	if (!props.iso) return <span className="text-muted-foreground text-xs">—</span>;
	return <span className="text-foreground tabular-nums">{formatIsoDate(props.iso)}</span>;
}

const PORTAL_LINKS_VISIBLE = 2;

function PortalLinks(props: { portals: PortalLink[] }) {
	if (props.portals.length === 0) {
		return <span className="text-muted-foreground text-xs">—</span>;
	}
	const hasMore = props.portals.length > PORTAL_LINKS_VISIBLE;
	const visible = hasMore ? props.portals.slice(0, PORTAL_LINKS_VISIBLE) : props.portals;
	const overflow = hasMore ? props.portals.slice(PORTAL_LINKS_VISIBLE) : [];
	return (
		<div className="flex flex-col gap-1">
			{visible.map((portal) => (
				<PortalLink key={portal.portalId} portal={portal} />
			))}
			{overflow.length > 0 && <PortalLinksOverflow portals={overflow} />}
		</div>
	);
}

function PortalLink(props: { portal: PortalLink }) {
	return (
		<a
			href={props.portal.url}
			target="_blank"
			rel="noopener noreferrer"
			className="text-primary hover:text-primary/80 inline-flex items-center gap-0.5 text-sm transition-colors"
		>
			<span className="truncate">{toTitleCase(props.portal.portalId)}</span>
			<ChevronRight className="size-3.5" />
		</a>
	);
}

function PortalLinksOverflow(props: { portals: PortalLink[] }) {
	const { t } = useLingui();
	const hiddenCount = props.portals.length;
	return (
		<Popover>
			<PopoverTrigger className="text-primary hover:text-primary/80 inline-flex cursor-pointer items-center self-start text-sm transition-colors">
				{t`+${hiddenCount} more`}
			</PopoverTrigger>
			<PopoverContent align="start" className="w-56 p-1">
				<div className="flex flex-col">
					{props.portals.map((portal) => (
						<a
							key={portal.portalId}
							href={portal.url}
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary hover:bg-muted inline-flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm transition-colors"
						>
							<span className="truncate">{toTitleCase(portal.portalId)}</span>
							<ChevronRight className="size-3.5 shrink-0" />
						</a>
					))}
				</div>
			</PopoverContent>
		</Popover>
	);
}

function toTitleCase(slug: string): string {
	return slug
		.split(/[\s_-]+/)
		.map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : part))
		.join(' ');
}

function TableHeaderCell(props: {
	label: string;
	isSorted?: boolean;
	sortOrder?: 'asc' | 'desc';
	onSort?: () => void;
}) {
	const sortable = !!props.onSort;
	const ariaSort: 'ascending' | 'descending' | 'none' | undefined = !sortable
		? undefined
		: props.isSorted
			? props.sortOrder === 'asc'
				? 'ascending'
				: 'descending'
			: 'none';
	return (
		<th className="px-5 py-3 text-left" aria-sort={ariaSort}>
			<button
				type="button"
				onClick={props.onSort}
				disabled={!sortable}
				className={cn(
					'inline-flex items-center gap-1 text-xs font-medium tracking-[0.08em] uppercase',
					sortable ? 'hover:text-foreground cursor-pointer' : 'cursor-default',
					props.isSorted ? 'text-foreground' : 'text-muted-foreground',
				)}
			>
				{props.label}
				{sortable && (
					<ChevronDown
						className={cn(
							'size-3.5 transition-transform',
							props.isSorted && props.sortOrder === 'asc' && 'rotate-180',
							!props.isSorted && 'opacity-50',
						)}
					/>
				)}
			</button>
		</th>
	);
}

function FundingChip(props: { funding: 'myPortal' | 'worldcoo' }) {
	const { t } = useLingui();
	const label = props.funding === 'myPortal' ? t`My Portal` : t`Worldcoo`;
	return (
		<span className="border-border text-foreground inline-flex h-7 items-center rounded-full border px-3 text-xs font-medium">
			{label}
		</span>
	);
}

function StatusBadge(props: { status: CampaignStatus }) {
	const { t } = useLingui();
	switch (props.status) {
		case 'funded':
			return (
				<span className="inline-flex items-center gap-1 text-sm font-medium text-[#1f6e3d]">
					<Check className="size-4 rounded-full bg-[#1f6e3d] p-0.5 text-white" strokeWidth={3} />
					{t`Funded`}
				</span>
			);
		case 'active':
			return <span className="text-foreground text-sm">{t`Active`}</span>;
		case 'notFundraising':
			return <span className="text-muted-foreground text-sm">{t`Not fundraising`}</span>;
		case 'paused':
			return <span className="text-sm text-[#8a5a1a]">{t`Paused`}</span>;
	}
}

function Pagination(props: { page: number; totalPages: number; onPageChange: (page: number) => void }) {
	const { t } = useLingui();
	const pages = Array.from({ length: props.totalPages }, (_, i) => i + 1);

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{pages.map((pageNum) => (
				<button
					key={pageNum}
					type="button"
					onClick={() => props.onPageChange(pageNum)}
					className={cn(
						'h-8 w-8 cursor-pointer rounded-md border text-sm font-medium transition-colors',
						pageNum === props.page
							? 'bg-primary text-primary-foreground border-primary'
							: 'border-border text-foreground hover:bg-muted',
					)}
				>
					{pageNum}
				</button>
			))}
			<button
				type="button"
				onClick={() => props.onPageChange(Math.min(props.page + 1, props.totalPages))}
				disabled={props.page >= props.totalPages}
				className="border-border text-foreground hover:bg-muted inline-flex h-8 cursor-pointer items-center gap-1 rounded-md border px-3 text-sm font-medium transition-colors disabled:cursor-default disabled:opacity-50"
			>
				{t`Next`}
				<ChevronRight className="size-4" />
			</button>
		</div>
	);
}

// ── Formatting helpers ──────────────────────────────────────────────────────

function formatEurosRounded(cents: number, locale: Locale): string {
	return formatCurrencyFromCents(locale, cents);
}

function formatEurosCompact(cents: number, locale: Locale): string {
	return formatCurrencyFromCents(locale, cents);
}

function formatShortMonth(yyyymmdd: string, locale: Locale): string {
	const [yearStr, monthStr] = yyyymmdd.split('-');
	if (!yearStr || !monthStr) return yyyymmdd;
	const date = new Date(Number(yearStr), Number(monthStr) - 1, 1);
	const month = date.toLocaleDateString(locale, { month: 'short' });
	return `${month} ${yearStr.slice(2)}`;
}

function formatLongMonth(yyyymmdd: string, locale: Locale): string {
	const [yearStr, monthStr] = yyyymmdd.split('-');
	if (!yearStr || !monthStr) return yyyymmdd;
	const date = new Date(Number(yearStr), Number(monthStr) - 1, 1);
	return date.toLocaleDateString(locale, { month: 'long' });
}

function formatShortDate(yyyymmdd: string, locale: Locale): string {
	const [yearStr, monthStr, dayStr] = yyyymmdd.split('-');
	if (!yearStr || !monthStr || !dayStr) return yyyymmdd;
	const date = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
	return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

function formatIsoDate(iso: string): string {
	const [yearStr, monthStr, dayStr] = iso.split('-');
	if (!yearStr || !monthStr || !dayStr) return iso;
	return `${dayStr.padStart(2, '0')}/${monthStr.padStart(2, '0')}/${yearStr}`;
}

// ── CSV download ────────────────────────────────────────────────────────────

type CsvHeaders = {
	nameHeader: string;
	revenueHeader: string;
	goalHeader: string;
	fundingHeader: string;
	statusHeader: string;
	startDateHeader: string;
	endDateHeader: string;
	merchantsHeader: string;
	portalsHeader: string;
};

function formatCentsAsEuros(cents: number): string {
	const sign = cents < 0 ? '-' : '';
	const abs = Math.abs(cents);
	const euros = Math.trunc(abs / 100);
	const remainder = abs % 100;
	return `${sign}${euros}.${remainder.toString().padStart(2, '0')}`;
}

function escapeCsvCell(value: string): string {
	// Defuse CSV formula injection: cells whose first char is a spreadsheet formula trigger
	// (`=`, `+`, `@`, `-`, tab, CR) are prefixed with a single quote so Excel/Sheets treats
	// them as literal text. See OWASP "CSV Injection".
	const injectionTriggers = /^[=+\-@\t\r]/;
	const safe = injectionTriggers.test(value) ? `'${value}` : value;
	if (safe.includes(',') || safe.includes('"') || safe.includes('\n')) {
		return `"${safe.replace(/"/g, '""')}"`;
	}
	return safe;
}

function buildCampaignsCsv(campaigns: CampaignRow[], headers: CsvHeaders): string {
	const head = [
		headers.nameHeader,
		headers.startDateHeader,
		headers.endDateHeader,
		headers.revenueHeader,
		headers.goalHeader,
		headers.fundingHeader,
		headers.merchantsHeader,
		headers.portalsHeader,
		headers.statusHeader,
	].map(escapeCsvCell);
	const rows = campaigns.map((c) =>
		[
			c.name,
			c.startDate ? formatIsoDate(c.startDate) : '',
			c.endDate ? formatIsoDate(c.endDate) : '',
			formatCentsAsEuros(c.revenueCents),
			c.goalCents != null ? formatCentsAsEuros(c.goalCents) : '',
			c.funding.join('; '),
			c.merchants.map((m) => m.alias).join('; '),
			c.portals.map((p) => `${p.label} (${p.url})`).join('; '),
			c.status,
		].map(escapeCsvCell),
	);
	return [head, ...rows].map((row) => row.join(',')).join('\n');
}

function triggerDownload(content: string, filename: string, mimeType: string) {
	// Prepend UTF-8 BOM so Excel opens the CSV with the correct encoding.
	const blob = new Blob(['\ufeff', content], { type: mimeType });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
}
